import { AppShell } from "@mantine/core";
import "@mantine/spotlight/styles.css";
import { useContext, useEffect, useState } from "react";
import {
  useDisclosure,
  useLocalStorage,
  useDocumentTitle,
} from "@mantine/hooks";
import { Navigate, Outlet, useMatches } from "react-router-dom";

import Header from "../components/Header/Header";
import Navbar from "../components/Navbar/Navbar";
import { TopHeader } from "../components/TopHeader/TopHeader";
import { SpotlightSwitcher } from "../components/Spotlight/SpotlightSwitcher";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { webConfig } from "../../config";
import { useEventLanguage } from "../hooks/useEventLanguage";

const NAVBAR_WIDTH = 280;
const NAVBAR_WIDTH_COLLAPSED = 70;

export default function App() {
  const { user } = useContext(AuthContext);
  const isAuthenticated = !!user;
  const matches = useMatches();
  const requiresEvent = matches.some((m) => m.handle?.requiresEvent);
  const hasEvent = !!getCurrentWorkspace(user)?.eventId;
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const [desktopCollapsed, setDesktopCollapsed] = useLocalStorage({
    key: "navbar-collapsed",
    defaultValue: false,
  });
  const [documentTitle, setTitle] = useState("CONGRS");
  useDocumentTitle(documentTitle);

  // Monitor current event and update language context
  useEventLanguage();

  // Set the document title to "CONGRS - DEV" in development environments
  // to make it easier to distinguish between production and development
  // because I have a habit of opening multiple environments in the browser.
  useEffect(() => {
    if (!webConfig.isProduction) {
      setTitle("CONGRS - DEV");
    }
  }, []);

  const toggleDesktopCollapse = () => {
    setDesktopCollapsed((prev) => !prev);
  };

  return (
    <>
      <SpotlightSwitcher />
      <TopHeader
        mobileOpened={mobileOpened}
        toggleMobile={toggleMobile}
        desktopCollapsed={desktopCollapsed}
        toggleDesktop={toggleDesktopCollapse}
        user={user}
      />
      <AppShell
        navbar={{
          width: {
            base: NAVBAR_WIDTH,
            sm: desktopCollapsed ? NAVBAR_WIDTH_COLLAPSED : NAVBAR_WIDTH,
          },
          breakpoint: "sm",
          collapsed: { mobile: !mobileOpened },
        }}
        padding="md"
        layout="alt"
        header={{
          height: 50,
        }}
        styles={{
          header: {
            top: 50,
          },
          navbar: {
            top: 50,
            height: "calc(100vh - 50px)",
          },
          main: {
            paddingTop: "calc(50px + 50px + var(--mantine-spacing-md))",
          },
        }}
      >
        <Header />

        <Navbar
          opened={mobileOpened}
          toggle={toggleMobile}
          user={user}
          isAuthenticated={isAuthenticated}
          collapsed={desktopCollapsed}
          toggleCollapse={toggleDesktopCollapse}
        />

        <AppShell.Main>
          {requiresEvent && !hasEvent ? (
            <Navigate to="/app" replace />
          ) : (
            <Outlet />
          )}
        </AppShell.Main>
      </AppShell>
    </>
  );
}
